import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  createContent,
  deleteContent,
  listContent,
  setContentVisible,
  updateContent,
} from "../../../services/api/contentAdminService";
import { ERROR_CODES } from "../../../services/errors/ApiError";
import { isAuthError, isCanceledError } from "../../../services/errors/errorHandler";
import { formatBusinessDateTime } from "../../../services/utils/dateUtils";
import AdminHeader from "../AdminHeader";
import Fields from "./FormFields";
import Revisions from "./Revisions";
import VisibleSwitch from "./VisibleSwitch";
import {
  collectionPath,
  CONTENT_HOME_PATH,
  fieldsFor,
  getCollection,
  itemPath,
  itemTitle,
  publicPathFor,
} from "./collections";
import { fieldDomId, serverFieldToPath, setIn, toFormValues, toPayload, validate } from "./formModel";
import { useUnsavedChangesWarning } from "./leaveGuard";
import "../admin.css";
import "./content.css";

const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const checkSlug = (slug) => {
  if (!slug) return "";
  if (slug.length > 60) return "Keep this to 60 characters or fewer.";
  return SLUG_RE.test(slug) ? "" : "Use small letters, numbers and single dashes only, like spring-group.";
};

const snapshot = (values, slug) => JSON.stringify({ values, slug });

/** The nearest input on the page for a path ('entries.2.phones.0' -> itself, or 'entries.2', ...). */
const nearestFieldPath = (path) => {
  let parts = path ? path.split(".") : [];
  while (parts.length) {
    const candidate = parts.join(".");
    if (document.getElementById(fieldDomId(candidate))) return candidate;
    parts = parts.slice(0, -1);
  }
  return "";
};

/**
 * Add or edit one content item (A.12): plain fields, Visible switch, save
 * with the stale-write guard, Earlier versions and Delete.
 *
 * @param {object} props
 * @param {string} props.collection
 * @param {string|null} props.id - null for a new item
 */
function ItemEditor({ collection, id }) {
  const meta = getCollection(collection);
  const isNew = id === null;
  const navigate = useNavigate();
  const location = useLocation();

  const [item, setItem] = useState(null);
  const [values, setValues] = useState(() => (isNew && meta && !meta.fixed ? toFormValues(meta.fields, {}) : null));
  const [slug, setSlug] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState(() => (values ? snapshot(values, "") : ""));
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [notice, setNotice] = useState(location.state && location.state.created ? "created" : "");
  const [visibleBusy, setVisibleBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [focusPath, setFocusPath] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fields = isNew ? (meta && meta.fields) || [] : fieldsFor(collection, item && item.slug);
  const dirty = values !== null && snapshot(values, slug) !== savedSnapshot;
  useUnsavedChangesWarning(dirty);

  const applyItem = useCallback(
    (next) => {
      const nextValues = toFormValues(fieldsFor(collection, next.slug), next.data);
      setItem(next);
      setValues(nextValues);
      setSlug(next.slug || "");
      setSavedSnapshot(snapshot(nextValues, next.slug || ""));
      setErrors({});
      setFormError("");
    },
    [collection]
  );

  useEffect(() => {
    if (isNew || !meta) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    listContent(collection, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        const found = (data.items || []).find((it) => String(it.id) === String(id));
        if (found) applyItem(found);
        else setLoadError("missing");
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        if (!isAuthError(err)) setLoadError(err.message || "Could not load this item.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [collection, id, isNew, meta, applyItem, reloadKey]);

  useEffect(() => {
    if (!focusPath) return;
    const el = document.getElementById(fieldDomId(focusPath));
    if (el) {
      el.focus();
      if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center" });
    }
    setFocusPath(null);
  }, [focusPath]);

  const onChange = useCallback((path, value) => {
    setValues((prev) => setIn(prev, path, value));
    setErrors((prev) => {
      if (!prev[path]) return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
    setJustSaved(false);
  }, []);

  const showErrors = (found) => {
    const paths = Object.keys(found);
    setErrors(found);
    setFormError(paths.length === 1 ? "Please fix the problem marked below." : `Please fix the ${paths.length} problems marked below.`);
    if (paths[0] === "slug") setAdvancedOpen(true);
    setFocusPath(paths[0]);
  };

  const onSaveError = (err) => {
    if (isAuthError(err)) return;
    if (err.code === ERROR_CODES.CONTENT_CONFLICT) {
      setConflict(true);
      return;
    }
    if (err.code === ERROR_CODES.CONFLICT) {
      showErrors({ slug: err.message });
      return;
    }
    if (err.code === ERROR_CODES.VALIDATION_ERROR && err.field) {
      const path = serverFieldToPath(err.field);
      const target = path === "slug" ? "slug" : nearestFieldPath(path);
      if (target) {
        showErrors({ [target]: err.message });
        return;
      }
    }
    setFormError(err.message || "Saving didn’t work. Please try again.");
  };

  const save = async (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    const found = validate(fields, values);
    const slugProblem = collection === "settings" ? "" : checkSlug(slug.trim());
    if (slugProblem) found.slug = slugProblem;
    if (Object.keys(found).length) {
      showErrors(found);
      return;
    }
    setSaving(true);
    setErrors({});
    setFormError("");
    setConflict(false);
    setNotice("");
    const data = toPayload(fields, values);
    const trimmedSlug = slug.trim();
    try {
      if (isNew) {
        const created = await createContent(collection, { data, slug: trimmedSlug || undefined });
        setSavedSnapshot(snapshot(values, slug));
        navigate(itemPath(collection, created.id), { replace: true, state: { created: true } });
        return;
      }
      const updated = await updateContent(collection, item.id, {
        data,
        slug: collection !== "settings" && trimmedSlug && trimmedSlug !== item.slug ? trimmedSlug : undefined,
        expectedUpdatedAt: item.updated_at,
      });
      applyItem(updated);
      setJustSaved(true);
    } catch (err) {
      onSaveError(err);
    } finally {
      setSaving(false);
    }
  };

  const loadLatest = () => {
    setConflict(false);
    setNotice("latest");
    setReloadKey((k) => k + 1);
  };

  const toggleVisible = async () => {
    setVisibleBusy(true);
    setFormError("");
    try {
      const updated = await setContentVisible(collection, item.id, !item.is_visible);
      // Keep her unsaved edits; take the new updated_at so the next save isn't refused.
      setItem((prev) => ({ ...prev, is_visible: updated.is_visible, updated_at: updated.updated_at }));
      setNotice(updated.is_visible ? "shown" : "hidden");
    } catch (err) {
      if (!isAuthError(err)) setFormError(err.message || "That didn’t work. Please try again.");
    } finally {
      setVisibleBusy(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await deleteContent(collection, item.id);
      navigate(collectionPath(collection), {
        state: { notice: `Deleted “${itemTitle(item)}”. You can bring it back from Deleted items.` },
      });
    } catch (err) {
      setDeleting(false);
      if (!isAuthError(err)) setFormError(err.message || "Could not delete it. Please try again.");
    }
  };

  const onRestored = useCallback(
    (restored, revision) => {
      applyItem(restored);
      setJustSaved(false);
      setNotice(`restored:${formatBusinessDateTime(revision.created_at)}`);
    },
    [applyItem]
  );

  if (!meta) {
    return (
      <EditorShell>
        <h1>Not found</h1>
        <p className="admin-muted">There’s no area of the site with that name.</p>
        <Link className="admin-btn" to={CONTENT_HOME_PATH}>Back to Site content</Link>
      </EditorShell>
    );
  }

  const backLink = (
    <Link className="cms-back" to={collectionPath(collection)}>
      ‹ {meta.label}
    </Link>
  );

  if (isNew && meta.fixed) {
    return (
      <EditorShell>
        {backLink}
        <h1>Can’t add here</h1>
        <p className="admin-muted">These three settings always exist. Open one to change it.</p>
      </EditorShell>
    );
  }

  if (loading) {
    return (
      <EditorShell>
        {backLink}
        <p className="admin-muted" role="status">Loading…</p>
      </EditorShell>
    );
  }

  if (loadError) {
    return (
      <EditorShell>
        {backLink}
        {loadError === "missing" ? (
          <>
            <h1>Not found</h1>
            <p className="admin-muted">This may have been deleted. Deleted items can be brought back from the list.</p>
          </>
        ) : (
          <div className="admin-error" role="alert">
            <p>{loadError}</p>
            <button type="button" className="admin-btn" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
          </div>
        )}
      </EditorShell>
    );
  }

  const title = isNew ? `New ${meta.noun}` : itemTitle(item);
  const publicPath = publicPathFor(collection, item ? item.slug : null, item ? item.data : null);
  const visible = Boolean(item && item.is_visible);

  let noticeText = "";
  if (notice === "created") noticeText = "Saved. It’s hidden on the site for now: switch “Visible on the site” on when you’re ready.";
  else if (notice === "shown") noticeText = "It’s on the site now. Changes show within a minute.";
  else if (notice === "hidden") noticeText = "It’s hidden from the site now.";
  else if (notice === "latest") noticeText = "Loaded the latest version.";
  else if (notice.startsWith("restored:")) noticeText = `Restored the version from ${notice.slice(9)}.`;

  const errorCount = Object.keys(errors).length;
  let saveStatus = "";
  if (saving) saveStatus = "Saving…";
  else if (justSaved && !dirty) saveStatus = "Saved";
  else if (dirty) saveStatus = "Not saved yet";

  return (
    <EditorShell>
      {backLink}
      <h1>{title}</h1>

      <div aria-live="polite" className="admin-live">
        {noticeText && <p className="admin-notice">{noticeText}</p>}
      </div>

      {!isNew && (
        <div className="cms-item-status">
          <VisibleSwitch id="cms-visible" checked={visible} busy={visibleBusy} onToggle={toggleVisible} />
          <p className="cms-hint">
            {visible ? "Visitors can see this." : "Hidden: visitors can’t see this."}{" "}
            <a href={publicPath} target="_blank" rel="noopener noreferrer">View on site</a>
          </p>
        </div>
      )}
      {isNew && <p className="admin-muted">New {meta.noun}s start hidden, so you can check them before anyone sees them.</p>}

      <form className="cms-form" onSubmit={save} noValidate aria-label={title}>
        <Fields fields={fields} values={values} errors={errors} onChange={onChange} />

        {collection !== "settings" && (
          <details className="cms-advanced" open={advancedOpen} onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}>
            <summary>Advanced</summary>
            <div className="cms-field">
              <label htmlFor={fieldDomId("slug")} className="cms-label">Web address name</label>
              <p id={`${fieldDomId("slug")}-hint`} className="cms-hint">
                The short name used in this item’s web address: small letters, numbers and dashes.
                {isNew ? " Leave it empty and one is made from the title." : " Most people never need to change it."}
              </p>
              <input
                id={fieldDomId("slug")}
                name="slug"
                type="text"
                className="cms-input"
                autoCapitalize="none"
                autoCorrect="off"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.slug;
                    return next;
                  });
                }}
                aria-invalid={errors.slug ? true : undefined}
                aria-describedby={[`${fieldDomId("slug")}-hint`, errors.slug && `${fieldDomId("slug")}-error`].filter(Boolean).join(" ")}
              />
              {errors.slug && <p id={`${fieldDomId("slug")}-error`} className="cms-field-error">{errors.slug}</p>}
            </div>
          </details>
        )}

        {formError && (
          <p className="admin-error" role="alert">
            {formError}
            {errorCount > 0 && <span className="sr-only"> The first one has the cursor.</span>}
          </p>
        )}

        {conflict && (
          <div className="admin-error" role="alert" data-testid="content-conflict">
            <p>
              This was changed somewhere else (maybe on another phone or browser tab) after you opened it, so your
              changes were <strong>not</strong> saved.
            </p>
            <p>Load the latest version, then make your changes again. Copy any long text you want to keep first.</p>
            <button type="button" className="admin-btn" onClick={loadLatest}>Load the latest version</button>
          </div>
        )}

        <div className="cms-savebar">
          <button type="submit" className="admin-btn admin-btn--primary cms-save" disabled={saving}>
            {saving ? "Saving…" : isNew ? `Save new ${meta.noun}` : "Save changes"}
          </button>
          <p className="cms-savebar__status" aria-live="polite" data-testid="save-status">
            {saveStatus === "Saved" ? (
              <>
                Saved.{" "}
                <a href={publicPath} target="_blank" rel="noopener noreferrer">View on site</a>
              </>
            ) : (
              saveStatus
            )}
          </p>
        </div>
      </form>

      {!isNew && item && <Revisions item={item} dirty={dirty} onRestored={onRestored} />}

      {!isNew && item && !meta.fixed && (
        <section className="cms-section" aria-label="Delete">
          {confirmDelete ? (
            <div className="admin-panel admin-panel--danger" role="group" aria-label="Confirm delete">
              <p className="admin-panel__title">Delete “{itemTitle(item)}”?</p>
              <p className="admin-panel__detail">It disappears from the site. You can bring it back later from Deleted items.</p>
              <div className="admin-actions">
                <button type="button" className="admin-btn admin-btn--danger" onClick={remove} disabled={deleting}>
                  {deleting ? "Deleting…" : "Yes, delete it"}
                </button>
                <button type="button" className="admin-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  Keep it
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="admin-btn admin-btn--danger" onClick={() => setConfirmDelete(true)}>
              Delete this {meta.noun}
            </button>
          )}
        </section>
      )}
    </EditorShell>
  );
}

function EditorShell({ children }) {
  return (
    <div className="admin">
      <AdminHeader />
      <main id="main" className="admin-main cms" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}

/**
 * Route element for /admin/content/:collection/:id and .../new. Keyed by
 * the route, so moving from "new" to the saved item starts a fresh editor.
 * @param {{isNew?: boolean}} props
 */
export default function ItemEditorRoute({ isNew = false }) {
  const { collection, id } = useParams();
  const key = `${collection}/${isNew ? "new" : id}`;
  return <ItemEditor key={key} collection={collection} id={isNew ? null : id} />;
}
